import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  Badge,
  Button,
  Flex,
  Icon,
  Skeleton,
  Text,
  cn,
  type IconType,
} from '@kognitos/lattice';
import { AutomationStageBadge } from '@/shared/components/AutomationStageBadge';
import { TooltipActionButton } from '@/shared/components/TooltipActionButton';

import { getAssetToneClass, type TAssetTone } from './asset-tone';
import styles from './AssetWorkspaceHeader.module.css';

/** The tone of an activity status: a dot, or the attention badge. */
export type TAssetActivityTone = 'running' | 'attention' | 'success' | 'error';

/**
 * Props for {@link AssetWorkspaceHeader}.
 */
export interface IAssetWorkspaceHeaderProps {
  /** Leaves the workspace, normally for the asset's catalog. */
  back: { label: string; onClick: () => void };
  /** An optional tile before the name with the asset type's icon, tinted with `tone`. */
  icon?: IconType;
  /** The type tile's colour, a lattice theme token. Defaults to `primary`. */
  tone?: TAssetTone;
  /** The asset's name. Empty while it loads, which shows a skeleton in its place. */
  title: string;
  /** Beside the name, e.g. the stage badge. */
  badges?: ReactNode;
  /** After the name and badges, e.g. the test workbench and map toggles. */
  titleActions?: ReactNode;
  /** Which view is showing; the switch at the end leads to the other. */
  view: 'definition' | 'activity';
  /** The Activity route, for the switch on Definition. */
  activityHref: string;
  /** The Definition route, for the switch on Activity. */
  definitionHref: string;
  /**
   * What needs the user, on the switch to Activity, e.g. "1 failed". Omit
   * when nothing does; the switch then shows the latest activity quietly.
   */
  attention?: { label: string; tone: 'attention' | 'error' };
  /** The most recent run, thread or refresh, e.g. "Last run 4 min ago". */
  latest?: { label: string; tone?: TAssetActivityTone };
  /** On the switch back to Definition, e.g. "Unpublished changes". */
  definitionBadge?: string;
  /** The switch back to Definition opens the live version: it reads "Document". */
  definitionIsLive?: boolean;
  /**
   * The live version, while Definition shows a draft of it. Adds a "Document"
   * pill before the switch that opens it.
   */
  liveHref?: string;
  /** Before the switch, e.g. Run and Publish on Activity. */
  actions?: ReactNode;
  'data-testid'?: string;
}

const PILL_CLASSES =
  'flex h-9 shrink-0 items-center gap-2 rounded-full border px-3 text-sm shadow-xs transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const TONE_DOT_CLASSES: Record<TAssetActivityTone, string> = {
  running: 'animate-pulse bg-primary',
  attention: 'bg-warning',
  success: 'bg-success',
  error: 'bg-destructive',
};

const ATTENTION_CLASSES = {
  attention: {
    badge: 'bg-warning/15 text-warning',
    border: 'border-warning',
  },
  error: {
    badge: 'bg-destructive/15 text-destructive',
    border: 'border-destructive',
  },
};

/**
 * The header across the top of an asset's workspace, on both Definition and
 * Activity: the way back, the asset's name, the view's actions, and a
 * switch to the other view that carries what needs the user.
 */
export function AssetWorkspaceHeader({
  back,
  icon,
  tone = 'primary',
  title,
  badges,
  titleActions,
  view,
  activityHref,
  definitionHref,
  attention,
  latest,
  definitionBadge,
  definitionIsLive,
  liveHref,
  actions,
  'data-testid': dataTestId = 'asset-workspace-header',
}: IAssetWorkspaceHeaderProps) {
  const { t } = useTranslation('common');

  return (
    <Flex
      align="center"
      gap={3}
      width="full"
      className="h-15 shrink-0 border-b border-border px-5"
      data-testid={dataTestId}
      data-view={view}
    >
      <Flex align="center" gap={2.5} className="min-w-0 flex-1">
        <TooltipActionButton
          tooltip={back.label}
          aria-label={back.label}
          icon="ArrowLeft"
          variant="ghost"
          size="icon-sm"
          onClick={back.onClick}
          data-testid="asset-workspace-header-back"
        />
        {icon && (
          <Flex
            align="center"
            justify="center"
            aria-hidden
            className={cn(
              getAssetToneClass(tone),
              styles.tile,
              'size-7 shrink-0 rounded-md',
            )}
          >
            <Icon type={icon} size="sm" />
          </Flex>
        )}
        <Flex align="center" gap={3} className="min-w-0">
          {title ? (
            <Text weight="medium" className="max-w-md truncate" title={title}>
              {title}
            </Text>
          ) : (
            // Same height as the name, so the header doesn't shift when it loads.
            <Skeleton
              className="h-6 w-48"
              data-testid="asset-workspace-header-title-skeleton"
            />
          )}
          {badges && (
            <Flex align="center" gap={1} className="shrink-0">
              {badges}
            </Flex>
          )}
          {titleActions && (
            <Flex align="center" gap={1} className="shrink-0">
              {titleActions}
            </Flex>
          )}
        </Flex>
      </Flex>

      {actions && (
        <Flex align="center" gap={2} className="shrink-0">
          {actions}
        </Flex>
      )}

      {liveHref && (
        <Button asChild variant="outline" className="shrink-0 rounded-full">
          {/* Replaced: the live version is the same asset, so Back leaves it. */}
          <Link to={liveHref} replace data-testid="asset-workspace-header-live">
            <Icon type="ListOrdered" size="sm" />
            <Text level="small" weight="medium">
              {t('common.assetWorkspace.liveDocument')}
            </Text>
            <LiveMark />
            <Icon type="ChevronRight" size="sm" />
          </Link>
        </Button>
      )}

      <AssetViewSwitch
        view={view}
        activityHref={activityHref}
        definitionHref={definitionHref}
        attention={attention}
        latest={latest}
        definitionBadge={definitionBadge}
        definitionIsLive={definitionIsLive}
      />
    </Flex>
  );
}

/** The Live stage badge, on a pill that opens the live version. */
function LiveMark() {
  const { t } = useTranslation('common');

  return (
    <AutomationStageBadge
      stage="published"
      label={t('common.assetStage.live')}
      data-testid="asset-workspace-live-mark"
    />
  );
}

/**
 * Props for {@link AssetViewSwitch}.
 */
export type IAssetViewSwitchProps = Pick<
  IAssetWorkspaceHeaderProps,
  | 'view'
  | 'activityHref'
  | 'definitionHref'
  | 'attention'
  | 'latest'
  | 'definitionBadge'
  | 'definitionIsLive'
>;

/**
 * The pill that switches an asset between its Draft (definition) view and
 * Activity: on the Draft it reads "Activity" with what needs the user, or the
 * latest activity quietly; on Activity it reads "Draft", or "Document" when it opens the live version. The workspace header
 * ends with it; a page outside the workspace (a published document) can use it
 * on its own.
 */
export function AssetViewSwitch({
  view,
  activityHref,
  definitionHref,
  attention,
  latest,
  definitionBadge,
  definitionIsLive,
}: IAssetViewSwitchProps) {
  const { t } = useTranslation('common');
  const isActivity = view === 'activity';

  return (
    <Link
      to={isActivity ? definitionHref : activityHref}
      className={cn(
        PILL_CLASSES,
        !isActivity && attention
          ? ATTENTION_CLASSES[attention.tone].border
          : 'border-primary/50 bg-primary/10',
      )}
      data-testid="asset-workspace-header-switch"
    >
      <Icon
        type={
          isActivity
            ? definitionIsLive
              ? 'ListOrdered'
              : 'FileText'
            : 'Activity'
        }
        size="sm"
      />
      <Text level="small" weight="medium">
        {isActivity
          ? definitionIsLive
            ? t('common.assetWorkspace.liveDocument')
            : t('common.assetWorkspace.definition')
          : t('common.assetWorkspace.activity')}
      </Text>
      {isActivity && definitionBadge && (
        <Badge variant="secondary" className="rounded-full">
          {definitionBadge}
        </Badge>
      )}
      {!isActivity && attention && (
        <Badge
          className={cn(
            'rounded-full border-0',
            ATTENTION_CLASSES[attention.tone].badge,
          )}
          data-testid="asset-workspace-header-attention"
        >
          {attention.label}
        </Badge>
      )}
      {!isActivity && !attention && (
        <Flex
          align="center"
          gap={1.5}
          className="min-w-0"
          data-testid="asset-workspace-header-latest"
        >
          <span
            aria-hidden
            className={cn(
              'size-1.5 shrink-0 rounded-full',
              latest?.tone
                ? TONE_DOT_CLASSES[latest.tone]
                : 'bg-muted-foreground',
            )}
          />
          <Text level="small" color="muted" className="truncate">
            {latest?.label ?? t('common.assetWorkspace.nothingYet')}
          </Text>
        </Flex>
      )}
      <Icon type="ChevronRight" size="sm" />
    </Link>
  );
}
